/**
 * VistoeraTASK-00589 — multi-tab Personal Overview center.
 * Builds on TASK-00586 identity / nickname / ledger APIs.
 * Tabs: home | plan | credits | (team if member_count>=2) | settings.
 * VistoeraTASK-00592 —「个人资料」deep-links via ?tab=settings&section=profile;
 * standalone account profile page and its edit modal removed.
 */
import { Alert, Button, Skeleton, Tabs } from 'antd'
import { lazy, Suspense, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { getTenantOverview } from '../api'
import { useStaleWhileRevalidate } from './backofficeResourceCache'
import { useMembershipFlow } from './contentProduction/canvas/membership/membershipFlowContext'
import CreditsTab from './overview/CreditsTab'
import OverviewHomeTab from './overview/OverviewHomeTab'
import PlanUsageTab from './overview/PlanUsageTab'
import SettingsTab from './overview/SettingsTab'
import TeamDirectoryTab from './overview/TeamDirectoryTab'
import { parseOverviewTab, resolveOverviewTabs, type OverviewTabKey } from './overview/overviewTabKeys'
import './overview/overview.css'

const UpgradePlanDrawer = lazy(() => import('./overview/UpgradePlanDrawer'))

export default function WorkspaceOverviewPage() {
  const { t } = useTranslation(['overview', 'actions'])
  const { workspaceId = '' } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    data: overview,
    isInitialLoading: loading,
    error: overviewError,
    reload: load,
  } = useStaleWhileRevalidate(workspaceId ? `overview:${workspaceId}` : null, () =>
    getTenantOverview(workspaceId).then((r) => r.data),
  )
  const error = overviewError ? overviewError.message || t('overview:loadFailed') : null

  const isTeam = overview?.identity?.workspace_kind === 'team'
  const allowedTabs = useMemo(() => resolveOverviewTabs(Boolean(isTeam)), [isTeam])
  const activeTab = parseOverviewTab(searchParams.get('tab'), Boolean(isTeam))

  const setTab = (key: OverviewTabKey) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'home') next.delete('tab')
    else next.set('tab', key)
    if (key !== 'settings') next.delete('section')
    else if (!next.get('section')) next.set('section', 'profile')
    setSearchParams(next, { replace: true })
  }

  const openPersonalProfile = () => {
    const next = new URLSearchParams(searchParams)
    next.set('tab', 'settings')
    next.set('section', 'profile')
    setSearchParams(next, { replace: false })
  }

  const [upgradeDrawerOpen, setUpgradeDrawerOpen] = useState(false)
  const upgradeCtaRef = useRef<HTMLButtonElement>(null)
  const { openMembership, setRestoreTarget } = useMembershipFlow()

  if (loading && !overview) {
    return (
      <div className="tc-ov-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 4 }} style={{ marginTop: 24 }} />
      </div>
    )
  }

  if (error && !overview) {
    return (
      <div className="tc-ov-page">
        <Alert type="error" showIcon message={t('overview:loadFailed')} description={error} />
        <Button style={{ marginTop: 12, width: 120 }} onClick={load}>
          {t('actions:retry')}
        </Button>
      </div>
    )
  }

  if (!overview) return null

  const tabItems = allowedTabs.map((key) => ({
    key,
    label: t(`overview:tabs.v3.labels.${key}`),
  }))

  const openPlan = () => {
    setRestoreTarget(upgradeCtaRef.current)
    setUpgradeDrawerOpen(true)
  }

  return (
    <div className="tc-ov-page tc-ov-v3" data-testid="tenant-overview-page">
      <div className="tc-ov-welcome">
        <div>
          <h1 className="tc-ov-welcome-title">{t('overview:tabs.v3.pageTitle')}</h1>
          <p className="tc-ov-welcome-sub">{t('overview:tabs.v3.pageSubtitle')}</p>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(k) => setTab(k as OverviewTabKey)}
        items={tabItems}
        className="tc-ov-v3-tabs"
        data-testid="overview-tabs"
      />

      {activeTab === 'home' ? (
        <OverviewHomeTab
          overview={overview}
          workspaceId={workspaceId}
          onEditProfile={openPersonalProfile}
          onManagePlan={openPlan}
          onOpenCredits={() => setTab('credits')}
        />
      ) : null}

      {activeTab === 'plan' ? (
        <PlanUsageTab
          overview={overview}
          onManagePlan={openPlan}
          isTeamMember={Boolean(isTeam) && !overview.identity?.can_manage_team}
        />
      ) : null}

      {activeTab === 'credits' ? <CreditsTab overview={overview} workspaceId={workspaceId} /> : null}

      {activeTab === 'team' && isTeam ? <TeamDirectoryTab workspaceId={workspaceId} /> : null}

      {activeTab === 'settings' ? <SettingsTab onProfileSaved={() => void load()} /> : null}

      {/* Hidden focus restore target for membership drawer */}
      <button ref={upgradeCtaRef} type="button" style={{ position: 'absolute', width: 0, height: 0, opacity: 0 }} aria-hidden />

      <Suspense fallback={null}>
        <UpgradePlanDrawer
          open={upgradeDrawerOpen}
          onClose={() => setUpgradeDrawerOpen(false)}
          workspaceId={workspaceId}
          restoreFocusRef={upgradeCtaRef}
          onOpenMembershipBenefits={openMembership}
        />
      </Suspense>
    </div>
  )
}
